import { useMemo } from 'react'

import { SkeletonBox } from '@circlefin/components'
import { y } from '@circlefin/form'
import { createFormDropdown } from '@circlefin/form/Form.Dropdown'
import { useCurrentCurrency } from '@features/locales/hooks/currency'
import {
  BlockchainAddressStatus,
  useBlockchainRecipientAddressesWithMetadataQuery,
} from '@shared/graphql'
import useTranslation from 'next-translate/useTranslation'

import type {
  BlockchainAbbreviation,
  BlockchainRecipientAddress,
} from '@shared/graphql'

export const BlockchainRecipientAddressesSchema =
  y.mixed<BlockchainRecipientAddress>()

export type RecipientAddressesFormValues = y.InferType<
  typeof BlockchainRecipientAddressesSchema
>

const Dropdown = createFormDropdown()

export type RecipientAddressesProps = Omit<
  React.ComponentProps<typeof Dropdown>,
  'items'
> & {
  /**
   * Selected blockchain.
   */
  blockchains?: BlockchainAbbreviation[]
}

export const RecipientAddresses: React.FC<RecipientAddressesProps> = ({
  label,
  blockchains,
  loading = false,
  ...props
}) => {
  const { t } = useTranslation('forms')
  const [{ currency }] = useCurrentCurrency()

  const { data, loading: recipientLoading } =
    useBlockchainRecipientAddressesWithMetadataQuery({
      variables: {
        filter: {
          chain:
            blockchains == null || blockchains.length <= 0
              ? undefined
              : blockchains,
          currency,
          status: [
            BlockchainAddressStatus.ACTIVE,
            BlockchainAddressStatus.ON_HOLD,
          ],
        },
      },
      fetchPolicy: 'cache-and-network',
    })

  const items = useMemo(() => {
    return (
      data?.blockchainRecipientAddressesWithMetadata.map((address) => ({
        label: address.description ?? '',
        value: address,
      })) ?? []
    )
  }, [data?.blockchainRecipientAddressesWithMetadata])

  return (
    <SkeletonBox className="h-10 w-full" loading={recipientLoading || loading}>
      <Dropdown
        data-testid="recipient-addresses-dropdown"
        items={items}
        label={label ?? t`dropdown.blockchain.recipientAddresses.label`}
        {...props}
      />
    </SkeletonBox>
  )
}
